/**
 * Legal page copy generators — Privacy Policy + Accessibility Statement.
 *
 * Every bespoke Fugue Lab client site ships a Privacy Policy and an
 * Accessibility Statement (footer-linked). The *copy* is single-sourced here so
 * it is correct, consistent, and honest about how these sites actually work
 * (static S3/CloudFront builds, contact forms that hand off to the visitor's own
 * mail app, no server-side storage of form data). The per-site *design* stays in
 * the site: a page component maps this structured `LegalDoc` through the site's
 * own typography (PageHero + prose), so privacy/accessibility match the build.
 *
 * Both generators return plain data (no JSX) so the same copy renders in a
 * bespoke React page, a CMS-published page, or a PDF.
 *
 * @example
 *   const doc = buildPrivacyPolicy({
 *     orgName: 'Samuel Sparrow',
 *     siteUrl: 'https://samuelsparrowclarinet.com',
 *     contactEmail: 'contact@samuelsparrowclarinet.com',
 *     lastUpdated: 'June 2026',
 *   })
 *   // doc.title, doc.intro[], doc.sections[{ heading, body[] }]
 */

/** One section of a legal document: a heading and one or more paragraphs. */
export interface LegalSection {
  heading: string
  /** Paragraphs. Each string is one `<p>`. Keep them plain text. */
  body: string[]
}

/** A rendered-agnostic legal document (privacy policy, accessibility statement). */
export interface LegalDoc {
  title: string
  /** Human-readable revision date, e.g. "June 2026". */
  lastUpdated: string
  /** Lead paragraph(s) shown before the first section. */
  intro: string[]
  sections: LegalSection[]
}

export interface PrivacyPolicyOptions {
  /** Organization / site owner name as it should read in prose. */
  orgName: string
  /** Canonical site URL, e.g. "https://example.com". */
  siteUrl: string
  /** Contact email for privacy requests. */
  contactEmail: string
  /** Human-readable revision date, e.g. "June 2026". */
  lastUpdated: string
  /**
   * The site has a contact form. Bespoke builds submit via the visitor's own
   * mail app (mailto:), so nothing is stored server-side. Default: true.
   */
  collectsContactForm?: boolean
  /**
   * How the contact form submits, which decides what the policy honestly claims:
   * - 'mailto' (default): hands off to the visitor's own mail app — nothing is
   *   stored on the site. True for the standard static bespoke build.
   * - 'relay': posts to a backend that FORWARDS it by email and keeps nothing.
   *   Fairmeadow Capital is the worked example — its Lambda calls SES and there
   *   is no database anywhere in the stack.
   * - 'server': posts to a backend that STORES it (the CMS forms rail: a
   *   submission row, a contact record, and a marketing-consent flag).
   *
   * ⚠️ 'relay' EXISTS BECAUSE THE OTHER TWO WERE BOTH WRONG FOR A REAL SITE,
   * and picking the nearest one is how this file acquired three false sentences
   * already. 'mailto' would deny that the message reaches a server of ours; it
   * does. 'server' would promise a stored record the visitor can ask to see;
   * there is none, so a subject-access request would be answered with "we have
   * nothing", after the policy said otherwise. A privacy policy is a factual
   * claim, and "close enough" is the failure mode, not the fix.
   */
  contactFormMode?: 'mailto' | 'relay' | 'server'
  /** The site has a newsletter / email signup. Default: false. */
  collectsNewsletter?: boolean
  /** The site takes donations or payments (e.g. an embedded processor). Default: false. */
  collectsDonations?: boolean
  /**
   * Analytics in use. 'google' adds a Google Analytics disclosure; 'none' (the
   * default) states no analytics/tracking cookies are used. Any other string is
   * named verbatim as the analytics provider.
   */
  analytics?: 'google' | 'none' | (string & {})
  /**
   * The contact form is protected by a captcha that runs third-party code in
   * the visitor's browser. Default: 'none'.
   *
   * This is NOT cosmetic detail. With `analytics: 'none'` the policy asserts
   * that the site "does not use advertising or tracking cookies" — which Google
   * reCAPTCHA makes FALSE the moment it is enabled, because it sets cookies and
   * reads behavioural signals to score the visitor. A policy that is wrong is
   * worse than a policy that is thin, and nothing else in this generator would
   * have noticed: the captcha lives on a form section, and this file only ever
   * sees the options it is handed.
   *
   * Only reCAPTCHA is implemented, so this is deliberately two-valued rather
   * than a provider list. A privacy-neutral captcha (Cloudflare Turnstile was
   * the one considered) would earn a SMALLER disclosure, not this one with the
   * vendor name swapped — so whoever adds it must write that paragraph rather
   * than inherit this one.
   */
  captcha?: 'recaptcha' | 'none'
  /** Optional postal address, appended to the contact section. */
  postalAddress?: string
  /**
   * Where submitted information is processed and stored, in prose — e.g.
   * "the United States". Adds a transfer-and-rights section.
   *
   * OPT-IN rather than always-on, deliberately. Every site on this platform is
   * hosted in AWS us-west-1, so the disclosure is true for all of them — but
   * switching it on by default would silently rewrite nineteen other clients'
   * LEGAL DOCUMENTS in a commit about one festival. Each site turns it on when
   * its owner has seen the wording.
   *
   * ⚠️ STATES FACTS ABOUT PROCESSING, NOT A LAWFUL BASIS. Which GDPR basis an
   * organisation relies on is a determination that organisation has to make;
   * asserting one here would be inventing legal advice. This says where the
   * data goes, who touches it, and what the reader can ask for.
   */
  dataProcessedIn?: string
}

export interface AccessibilityStatementOptions {
  /** Organization / site owner name as it should read in prose. */
  orgName: string
  /** Contact email for accessibility feedback. */
  contactEmail: string
  /** Human-readable revision date, e.g. "June 2026". */
  lastUpdated: string
  /** Conformance target. Default: "WCAG 2.1 Level AA". */
  standard?: string
  /** Optional phone number for accessibility requests. */
  phone?: string
  /** Optional postal address, appended to the contact section. */
  postalAddress?: string
}

/**
 * Build a Privacy Policy for a bespoke Fugue Lab client site.
 *
 * The default copy describes a static site that does not store personal data
 * server-side — which is how these builds actually behave — and layers in
 * disclosures only for the features the site has (newsletter, donations,
 * analytics). This keeps the policy honest rather than copying a generic
 * SaaS-style policy that promises data handling the site never does.
 */
export function buildPrivacyPolicy(options: PrivacyPolicyOptions): LegalDoc {
  const {
    orgName,
    siteUrl,
    contactEmail,
    lastUpdated,
    collectsContactForm = true,
    contactFormMode = 'mailto',
    collectsNewsletter = false,
    collectsDonations = false,
    analytics = 'none',
    captcha = 'none',
    postalAddress,
    dataProcessedIn,
  } = options

  const sections: LegalSection[] = []

  const collected: string[] = []
  if (collectsContactForm) {
    collected.push(
      contactFormMode === 'server'
        ? 'When you submit a contact or inquiry form on this site, the information you enter (such as your name, email address, and message) is sent to us so we can respond. We use it only to reply to you and to provide what you asked for; we do not sell it or share it with unrelated third parties.'
        : contactFormMode === 'relay'
          ? 'When you submit a contact form on this site, the information you enter (such as your name, email address, and message) is sent to us by email so we can respond. It is not saved to a database on this website — it arrives in our inbox and stays there. We use it only to reply to you, and we do not sell it or share it with unrelated third parties.'
          : 'When you use a contact form on this site, your message opens in your own email application and is sent directly to us. The form does not store your information on this website or pass it to any third party — we receive only the email you choose to send.'
    )
  }
  if (collectsNewsletter) {
    collected.push(
      'If you sign up for our email list, we collect the email address you provide so we can send you updates. You can unsubscribe at any time using the link in any email we send.'
    )
  }
  if (collectsDonations) {
    collected.push(
      'If you make a donation or payment through this site, the transaction is handled by a third-party payment processor. We do not see or store your full card details; the processor handles your payment information under its own privacy policy.'
    )
  }
  collected.push(
    `We do not sell, rent, or trade your personal information. We use what you send us only to respond to you and to provide the services you requested.`
  )

  sections.push({
    heading: 'Information we collect',
    body: collected,
  })

  if (analytics === 'none') {
    sections.push({
      heading: 'Cookies and analytics',
      body: [
        // The unqualified claim only holds when NOTHING third-party runs in the
        // visitor's browser. reCAPTCHA does, so the sentence is narrowed rather
        // than left standing next to a contradicting section further down. A
        // privacy policy that contradicts itself is worse than a vague one.
        captcha === 'recaptcha'
          ? 'This site does not use advertising cookies and does not build a profile of your activity across other websites for our own purposes. The one exception is the spam protection on our contact form, described below.'
          : 'This site does not use advertising or tracking cookies, and it does not build a profile of your activity across other websites.',
      ],
    })
  } else if (analytics === 'google') {
    sections.push({
      heading: 'Cookies and analytics',
      body: [
        'We use Google Analytics to understand how visitors use the site in aggregate (for example, which pages are most visited). Google Analytics sets cookies and may collect anonymized usage data under Google’s privacy policy. We use this only to improve the site and never to identify you personally.',
        'You can opt out of Google Analytics using the browser add-on Google provides, or by blocking cookies in your browser settings.',
      ],
    })
  } else {
    sections.push({
      heading: 'Cookies and analytics',
      body: [
        `We use ${analytics} to understand how visitors use the site in aggregate. It may set cookies and collect anonymized usage data under its own privacy policy. We use this only to improve the site, never to identify you personally.`,
      ],
    })
  }

  // Named as its own section rather than folded into the analytics one: this is
  // a different purpose (fraud prevention, not measurement) with a different
  // legal basis, and burying "Google reads your behaviour on this page" inside a
  // paragraph headed "analytics" is the kind of accurate-but-hidden disclosure a
  // policy exists to avoid.
  if (captcha === 'recaptcha') {
    sections.push({
      // ⚠️ "FORMS", NOT "THE CONTACT FORM". This section was written when a
      // captcha could only be on one page, and it named that page three times.
      // Chroma now protects its contact form AND the mailing-list signup on the
      // home page, so "our contact form" / "on the contact page" told visitors
      // the cookie was set somewhere it was not, and not set where it was.
      //
      // Third instance of this class in this file, after "your own email
      // provider" and "stores very little" — copy that assumed a narrower setup
      // than the platform grew into. That is what the document-wide tests are
      // for: each of the three sat in a different section.
      heading: 'Spam protection on our forms',
      body: [
        'Our forms are protected by Google reCAPTCHA, which helps us tell real visitors from automated spam. To do that, Google collects information about your device and your interaction with the page — including your IP address — and may set cookies in your browser. This happens on any page carrying one of those forms, whether or not you send us anything.',
        'Google processes that information under its own Privacy Policy (policies.google.com/privacy) and Terms of Service (policies.google.com/terms). We use it only to keep spam out of our inbox; it is not used to identify you, to advertise to you on our behalf, or for any other purpose by us.',
        'If you would rather not use reCAPTCHA, you can email us directly at the address below instead of using a form.',
      ],
    })
  }

  sections.push({
    heading: 'How we protect your information',
    body: [
      // ⚠️ THIS SENTENCE MUST FOLLOW `contactFormMode`. It was unconditional
      // until 2026-09-04 and asserted the MAILTO behaviour — "delivered through
      // your own email provider" — on every site, including the five already
      // submitting to our servers. A privacy policy is a factual claim about
      // data handling, so a stale default here is not a wording nit: it told
      // visitors their message never reached us when it did.
      contactFormMode === 'server'
        ? 'This site is served over an encrypted (HTTPS) connection. When you submit a form, your message is transmitted to our servers, stored so that we can act on it, and forwarded to us by email. We keep the information you send us only as long as needed to respond and follow up.'
        : contactFormMode === 'relay'
          ? 'This site is served over an encrypted (HTTPS) connection. When you submit a form, your message is transmitted to our servers and forwarded straight to us by email; it is not stored on this website. We keep the information you send us only as long as needed to respond and follow up.'
          : 'This site is served over an encrypted (HTTPS) connection. Any message you send us is delivered through your own email provider. We keep the information you send us only as long as needed to respond and follow up.',
    ],
  })

  sections.push({
    heading: 'Third-party links',
    body: [
      'This site may link to other websites — for example, ticketing, streaming, or social-media pages. We are not responsible for the privacy practices of those sites, and we encourage you to review their policies before sharing information with them.',
    ],
  })

  sections.push({
    heading: 'Children’s privacy',
    body: [
      'This site is not directed to children under 13, and we do not knowingly collect personal information from children. If you believe a child has sent us personal information, please contact us and we will delete it.',
    ],
  })

  if (dataProcessedIn) {
    sections.push({
      heading: 'Where your information is processed',
      body: [
        // ⚠️ THE THIRD SENTENCE IN THIS FILE TO ASSUME A SERVER FORM, and it
        // arrived AFTER the two document-wide tests that exist because of the
        // other two. On a mailto site nothing you type reaches our hosting —
        // the message goes from your own mail client to whatever mailbox the
        // organisation reads, which may not be AWS and is not ours to describe.
        // The site itself is still hosted here, so the location claim stands;
        // what changes is what is processed at it.
        contactFormMode === 'mailto'
          ? `This website is hosted in ${dataProcessedIn}, on Amazon Web Services. Messages you send us go through your own email provider to our mailbox rather than through this site, so they are not stored here.`
          : `This website and the messages you send through it are hosted and processed in ${dataProcessedIn}. Our hosting and email delivery are provided by Amazon Web Services, which ${contactFormMode === 'relay' ? 'carries the message on our behalf' : 'stores the information on our behalf'} and does not use it for its own purposes.`,
        contactFormMode === 'server'
          ? `If you are writing to us from outside ${dataProcessedIn} — including from the United Kingdom or the European Economic Area — your information is transferred there so that we can receive and answer it. You can ask us at any time to tell you what we hold, to correct it, to delete it, or to stop using it, and you can withdraw consent to our email list without affecting anything you have already asked us for.`
          : contactFormMode === 'relay'
            ? `If you are writing to us from outside ${dataProcessedIn} — including from the United Kingdom or the European Economic Area — your message is transferred there so that we can receive and answer it. You can ask us at any time to tell you what we hold, to correct it, to delete it, or to stop using it.`
            : `If you are visiting from outside ${dataProcessedIn} — including from the United Kingdom or the European Economic Area — the pages you request are served from there. You can ask us at any time to tell you what we hold, to correct it, to delete it, or to stop using it.`,
      ],
    })
  }

  sections.push({
    heading: 'Your choices',
    body: [
      // ⚠️ SECOND CLAIM IN THIS DOCUMENT THAT ASSUMED MAILTO. "Stores very
      // little" is true of a site that only opens the visitor's mail client and
      // false of one that keeps submissions, contact records and a consent log.
      // Found the same day as the "your own email provider" sentence, in a
      // different section, which is why the test for this is document-wide.
      contactFormMode === 'server'
        ? 'You may contact us at any time to ask what information we hold about you, to correct it, or to ask us to delete it. We keep the messages you send us, the contact details you give us, and a record of whether you asked to join our email list, so a request to see or delete that is something we can act on directly.'
        : contactFormMode === 'relay'
          ? 'You may contact us at any time to ask what information we hold about you, to correct it, or to ask us to delete it. What we hold is the email your form submission became, so this is usually as simple as asking us to delete it.'
          : 'You may contact us at any time to ask what information we hold about you, to correct it, or to ask us to delete it. Because this site stores very little, this is usually as simple as asking us to remove an email you sent.',
    ],
  })

  const contactLines = [
    `If you have any questions about this Privacy Policy or how your information is handled, please contact ${orgName} at ${contactEmail}.`,
  ]
  if (postalAddress) contactLines.push(postalAddress)
  sections.push({ heading: 'Contact us', body: contactLines })

  sections.push({
    heading: 'Changes to this policy',
    body: [
      'We may update this Privacy Policy from time to time. When we do, we will revise the date below. Your continued use of the site after a change means you accept the updated policy.',
    ],
  })

  return {
    title: 'Privacy Policy',
    lastUpdated,
    intro: [
      `${orgName} ("we," "us," or "our") respects your privacy. This policy explains what information we collect when you visit ${siteUrl}, how we use it, and the choices you have.`,
    ],
    sections,
  }
}

/**
 * Build an Accessibility Statement for a bespoke Fugue Lab client site.
 *
 * Bespoke builds are graded against the `@gjl/quality` rendered accessibility
 * baseline (semantic landmarks, alt text, ≥44px tap targets, ≥4.5:1 contrast,
 * keyboard operability, responsive layout), so this statement can truthfully
 * claim an active commitment and a concrete conformance target — and invites
 * feedback in the LGMF-style "in writing, by email, or by phone" tone.
 */
export function buildAccessibilityStatement(options: AccessibilityStatementOptions): LegalDoc {
  const { orgName, contactEmail, lastUpdated, standard = 'WCAG 2.1 Level AA', phone, postalAddress } = options

  const contactChannels = ['by email at ' + contactEmail]
  if (phone) contactChannels.push('by phone at ' + phone)
  if (postalAddress) contactChannels.push('in writing at ' + postalAddress)
  const contactSentence =
    contactChannels.length === 1
      ? contactChannels[0]
      : contactChannels.slice(0, -1).join(', ') + ', or ' + contactChannels[contactChannels.length - 1]

  return {
    title: 'Accessibility Statement',
    lastUpdated,
    intro: [
      `${orgName} is committed to making this website accessible to the widest possible audience, regardless of ability or technology. We want every visitor to be able to find information, navigate, and reach us with ease.`,
    ],
    sections: [
      {
        heading: 'Our standard',
        body: [
          `We aim to conform to the Web Content Accessibility Guidelines (${standard}). These guidelines explain how to make web content more accessible for people with disabilities and more usable for everyone.`,
        ],
      },
      {
        heading: 'What we’ve done',
        body: [
          'This site is built with accessibility in mind from the start. That includes semantic page structure and landmarks for screen readers, descriptive alternative text on meaningful images, sufficient color contrast for text, touch targets large enough to use comfortably, full keyboard operability, and a responsive layout that adapts to phones, tablets, and desktops.',
        ],
      },
      {
        heading: 'Ongoing effort',
        body: [
          'Accessibility is an ongoing commitment rather than a one-time project. We review the site as we add content, and some material may not yet meet the strictest standards. If you come across anything that is difficult to access, please tell us — your feedback helps us fix issues quickly and improve the site for everyone.',
        ],
      },
      {
        heading: 'Contact us',
        body: [
          `If you have difficulty accessing any part of this site, or you have a suggestion that would help, please contact ${orgName} ${contactSentence}. We will work with you directly to provide the information you need in a format that works for you.`,
        ],
      },
    ],
  }
}
